import { useMemo, useState } from "react";
import { CheckCircle2, XCircle, PlaneTakeoff, BookUser, RotateCcw, User, Users } from "lucide-react";
import { Label } from "@/components/ui/label";
import { DateInput, parseDMY } from "@/components/DateInput";
import { GroupCheck } from "@/components/GroupCheck";
import { fmtDate as fmt, minExpiry, gap } from "@/lib/passport";
import { PASSPORT } from "@/constants/testIds";

const DateField = ({ id, label, icon: Icon, value, onChange, testid }) => {
  const invalid = value.length === 10 && !parseDMY(value);
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="overline-label flex items-center gap-2"><Icon className="h-3.5 w-3.5" />{label}</Label>
      <DateInput id={id} value={value} onChange={onChange} invalid={invalid} data-testid={testid} />
      {invalid && <p className="text-xs text-red-600">Tanggal tidak valid</p>}
    </div>
  );
};

const Result = ({ dep, exp }) => {
  const min = minExpiry(dep);
  const valid = exp >= min;
  const tone = valid ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-red-200 bg-red-50 text-red-700";
  const Icon = valid ? CheckCircle2 : XCircle;
  return (
    <div className={`fade-up rounded-lg border p-6 sm:p-8 ${tone}`} data-testid={PASSPORT.result}>
      <div className="flex items-center gap-4">
        <Icon className="h-12 w-12 shrink-0" />
        <div>
          <p className="overline-label opacity-80">Status paspor</p>
          <p className="font-heading text-4xl sm:text-5xl font-bold tracking-tight leading-none mt-1" data-testid={PASSPORT.status}>{valid ? "VALID" : "EXPIRED"}</p>
        </div>
      </div>
      <dl className="mt-6 grid gap-4 text-sm text-slate-700 sm:grid-cols-2">
        <div><dt className="text-xs text-slate-500">Sisa masa berlaku saat berangkat</dt><dd className="font-medium mt-1" data-testid={PASSPORT.remaining}>{gap(dep, exp)}</dd></div>
        <div><dt className="text-xs text-slate-500">Paspor minimal berlaku sampai</dt><dd className="font-medium mt-1" data-testid={PASSPORT.minDate}>{fmt(min)}</dd></div>
      </dl>
    </div>
  );
};

const Tab = ({ active, onClick, icon: Icon, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "bg-brand text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
    <Icon className="h-4 w-4" />{children}
  </button>
);

export default function PassportCheck() {
  const [mode, setMode] = useState("single");
  const [dep, setDep] = useState("");
  const [exp, setExp] = useState("");
  const parsed = useMemo(() => ({ dep: parseDMY(dep), exp: parseDMY(exp) }), [dep, exp]);

  return (
    <div className="max-w-4xl space-y-8">
      <div className="fade-up">
        <p className="overline-label">Program CS</p>
        <h1 className="font-heading mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">Cek Masa Berlaku Paspor</h1>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">Paspor dinyatakan <b>VALID</b> jika masih berlaku minimal 6 bulan 20 hari dari tanggal keberangkatan.</p>
      </div>
      <div className="inline-flex gap-1 rounded-full border border-slate-200 bg-white p-1 fade-up">
        <Tab active={mode === "single"} onClick={() => setMode("single")} icon={User} testid={PASSPORT.tabSingle}>Satu Paspor</Tab>
        <Tab active={mode === "group"} onClick={() => setMode("group")} icon={Users} testid={PASSPORT.tabGroup}>Rombongan</Tab>
      </div>
      <div className="rounded-lg border border-slate-200 bg-white p-5 sm:p-6 space-y-6 fade-up" style={{ animationDelay: "60ms" }}>
        <div className="grid gap-6 sm:grid-cols-2">
          <DateField id="dep" label="Tanggal keberangkatan" icon={PlaneTakeoff} value={dep} onChange={setDep} testid={PASSPORT.departureInput} />
          {mode === "single" && <DateField id="exp" label="Tanggal expired paspor" icon={BookUser} value={exp} onChange={setExp} testid={PASSPORT.expiryInput} />}
        </div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-xs text-slate-500">Format: DD MM YYYY, contoh 17 08 2026</p>
          <button onClick={() => { setDep(""); setExp(""); }} className="flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition-colors" data-testid={PASSPORT.reset}>
            <RotateCcw className="h-3.5 w-3.5" /> Reset
          </button>
        </div>
      </div>
      {mode === "group" ? (
        <GroupCheck dep={parsed.dep} />
      ) : parsed.dep && parsed.exp ? (
        <Result dep={parsed.dep} exp={parsed.exp} />
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white px-6 py-10 text-center text-sm text-slate-500" data-testid={PASSPORT.placeholder}>
          Isi kedua tanggal untuk melihat hasil pengecekan.
        </div>
      )}
    </div>
  );
}
